/// <reference path="ShellProtocol.ts" />

/**
 * Manager side of the handoff: runs when this build was opened by the global shell
 * (AppMode = manager, ?embedded=1, inside an iframe).
 *
 * It announces itself to the shell, adopts the session the shell posts back, opens
 * the business and acknowledges. While it waits, the login is not shown; if no
 * session arrives it falls back to the normal login, so the URL still works when
 * somebody opens it by hand.
 */
class EmbeddedSessionController
{
    private static _sharedInstance:EmbeddedSessionController = null;
    static sharedInstance():EmbeddedSessionController {
        if (EmbeddedSessionController._sharedInstance == null) {
            EmbeddedSessionController._sharedInstance = new EmbeddedSessionController();
        }
        return EmbeddedSessionController._sharedInstance;
    }

    // The shell may attach its listener a moment after the iframe starts, so the
    // ready message is repeated until a session arrives or we give up.
    private static readonly ReadyInterval  = 1000;
    private static readonly SessionTimeout = 5000;

    private shellOrigin:string = null;
    private readyInterval:number = null;
    private startedAt = 0;

    start() {
        window.addEventListener("message", (ev:MessageEvent) => this.messageReceived(ev));
        MIONotificationCenter.defaultCenter().addObserver(this, "ShowLoginController", this.sessionExpired);

        this.startedAt = Date.now();
        this.postReady();
        this.readyInterval = window.setInterval(() => {
            if (Date.now() - this.startedAt > EmbeddedSessionController.SessionTimeout) {
                this.abandon();
                return;
            }
            this.postReady();
        }, EmbeddedSessionController.ReadyInterval);
    }

    /** Topbar workspace button: the shell owns the picker while we are embedded. */
    showWorkspace() {
        this.post(ShellMessage.ShowWorkspace, {});
    }

    private sessionExpired() {
        this.post(ShellMessage.SessionExpired, {});
    }

    private postReady() {
        let message = { "type": ShellMessage.ManagerReady, "version": this.appVersion() };
        // postMessage drops the message silently when the target origin does not match
        // the parent, so announcing to every allowed shell origin is safe.
        for (let origin of SettingsHelper.sharedInstance().shellOrigins) {
            window.parent.postMessage(message, origin);
        }
    }

    private appVersion():string {
        let data = MIOBundle.mainBundle().pathForResourceOfType("app", "plist");
        let config = MIOPropertyListSerialization.propertyListWithData(data, 0, 0, null);
        return config != null ? config["Version"] : null;
    }

    private stopReadyLoop() {
        if (this.readyInterval == null) return;
        window.clearInterval(this.readyInterval);
        this.readyInterval = null;
    }

    /** Nobody handed a session over: behave like a direct visit and show the login. */
    private abandon() {
        this.stopReadyLoop();
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        ad.embedded = false;
        (ad.window.rootViewController as MainViewController).showLoginViewController();
    }

    private messageReceived(ev:MessageEvent) {
        if (ev.source !== window.parent) return;
        if (SettingsHelper.sharedInstance().shellOrigins.indexOf(ev.origin) == -1) return;

        let message = ev.data;
        if (message == null || typeof message != "object") return;

        if (message["type"] == ShellMessage.Session) {
            this.shellOrigin = ev.origin;
            this.adoptSession(message as ShellSession);
        }
    }

    private adoptSession(session:ShellSession) {
        this.stopReadyLoop();

        let business = session.business;
        if (session.accessToken == null || business == null || business["id"] == null) {
            this.post(ShellMessage.SessionError, { "reason": "invalid-session" });
            return;
        }

        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        DualLinkAuthKit.sharedInstance().adoptSession(session.accessToken, session.user);
        ad.loginOK = true;

        let businessID = (business["id"] as string).toUpperCase();
        if (ad.selectedIdentifier == businessID) {
            // Same workspace, fresh token (the shell renewed an expired session).
            this.post(ShellMessage.SessionAck, { "businessID": businessID });
            return;
        }

        ad.setBusiness(business, this, function(this:EmbeddedSessionController) {
            this.post(ShellMessage.SessionAck, { "businessID": businessID });
        });
    }

    private post(type:string, payload:any) {
        if (this.shellOrigin == null) return;
        let message = payload != null ? payload : {};
        message["type"] = type;
        window.parent.postMessage(message, this.shellOrigin);
    }
}
